/**
 * The app's three global states and the order in which they win.
 *
 * A **takeover** replaces the app's rendered content; an **overlay** renders
 * above mounted route state without unmounting it (CONTEXT.md). Maintenance is
 * the takeover; offline and session-expired are the overlays.
 *
 * Lives in `lib/` — isomorphic, no React and no router — so the QueryClient
 * error handlers (which run outside React entirely) and the root component can
 * both reach the same store without either importing the other.
 */

import { isUnauthorizedError } from "@/lib/errors";

/**
 * Precedence, highest first. The array *is* the rule: `resolveGlobalState`
 * reads it in order, so reordering these three entries is the only way to
 * change which surface a user meets when states collide.
 */
export const GLOBAL_STATE_PRECEDENCE = ["maintenance", "offline", "session-expired"] as const;

export type GlobalState = (typeof GLOBAL_STATE_PRECEDENCE)[number];

/** Which of the three are currently active — one flag per state, no ordering. */
export type GlobalStateFlags = Record<GlobalState, boolean>;

/**
 * The single active global state, or null when none is. Keying the flags by
 * the same literals the precedence array holds is what keeps the two from
 * drifting: a new state cannot be added to one without the other.
 */
export function resolveGlobalState(flags: GlobalStateFlags): GlobalState | null {
  return GLOBAL_STATE_PRECEDENCE.find((state) => flags[state]) ?? null;
}

export interface GlobalStateStore {
  /** Register a listener; returns its unsubscribe. `useSyncExternalStore`. */
  subscribe(listener: () => void): () => void;
  /** The active state in the browser, or null. */
  getSnapshot(): GlobalState | null;
  /** The active state during SSR and hydration, or null. */
  getServerSnapshot(): GlobalState | null;
  activate(state: GlobalState): void;
  deactivate(state: GlobalState): void;
  /**
   * Deactivate a state and refuse to raise it again for this store's lifetime.
   *
   * For the moment a state stops being news. Once the visitor has been handed
   * off to sign in, work that was already in flight goes on failing — a
   * mutation the click could not cancel comes back unauthorized a moment later
   * — and re-raising the overlay then would drop a modal surface on top of the
   * sign-in page, locking them out of the one thing that could fix it. A store
   * lifetime is the right bound: sign-in is a full-document navigation, so the
   * next router gets a clean one.
   */
  retire(state: GlobalState): void;
}

/**
 * One store per router — per request on the server, per app boot on the client,
 * exactly like the QueryClient it is constructed beside (router.tsx). Never a
 * module-level singleton: an SSR pass would leak one request's state into the
 * next.
 *
 * The maintenance slot is wired here and stays inert — nothing activates it
 * until the takeover lands (#153). Its place in the precedence order is the
 * part that has to exist now, so the takeover ticket adds a source, not a rule.
 */
export function createGlobalStateStore(): GlobalStateStore {
  const flags: GlobalStateFlags = {
    maintenance: false,
    offline: false,
    "session-expired": false,
  };
  const listeners = new Set<() => void>();
  const retired = new Set<GlobalState>();

  function set(state: GlobalState, active: boolean) {
    if (active && retired.has(state)) return;
    // `useSyncExternalStore` re-reads the snapshot on every notification and
    // bails on an unchanged value, so a no-op write is only wasted work — but
    // the browser fires `online`/`offline` in bursts, so skip it at the source.
    if (flags[state] === active) return;
    flags[state] = active;
    for (const listener of listeners) listener();
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => resolveGlobalState(flags),
    // An overlay renders *above mounted route state*, and nothing is mounted
    // until the browser has hydrated — so neither has a first paint to be part
    // of, and both are pinned off here rather than read from the flags. Only
    // the takeover survives into SSR. React reads this during hydration too,
    // which is what keeps the server and client renders in agreement without a
    // special case at the call site. Precedence still comes from the one place.
    getServerSnapshot: () =>
      resolveGlobalState({
        maintenance: flags.maintenance,
        offline: false,
        "session-expired": false,
      }),
    activate: (state) => set(state, true),
    deactivate: (state) => set(state, false),
    retire: (state) => {
      set(state, false);
      retired.add(state);
    },
  };
}

/**
 * The rule that raises the session-expired overlay, as a handler the
 * QueryClient's `QueryCache` and `MutationCache` both take (`router.tsx`).
 *
 * It lives beside the store rather than at the composition root so the rule and
 * the state it flips are one unit — and so a test can wire a QueryClient the
 * same way the router does without dragging the route tree in behind it.
 */
export function unauthorizedInterceptor(store: GlobalStateStore) {
  return (error: unknown) => {
    if (isUnauthorizedError(error)) store.activate("session-expired");
  };
}
